/**
 * Rubik + Stretch packshot order fix (staging) — v2
 *
 * After Rubik Variant Images hides unassigned PDP media, Stretch's
 * grid_half_highlight_first hero uses:
 *   .product-gallery__media:nth-child(1 of :not([hidden]))
 * and the mobile carousel uses live DOM / allCells order.
 *
 * v1 only moved the packshot node (and often lost to Stretch/Rubik index
 * select), so the correct image could be selected while still sitting at
 * visible index ~3. v2 physically reorders every assigned
 * `.product-gallery__media` (and matching thumbs/dots) into Rubik
 * assignment order — packshot first — then refreshes Stretch filter/select
 * so position 0 is the packshot on desktop and mobile.
 *
 * No-op when Rubik assignment JSON is absent or nothing is filtered.
 */
(function () {
  'use strict';

  window.__RUBIK_PACKSHOT_ORDER_V = 2.2;

  var MEDIA_SEL = '.product-gallery__media';
  var THUMB_SEL = 'button.scroll-marker';
  var DEBOUNCE_MS = 50;
  var pending = null;
  var busy = false;
  var stamped = new WeakSet();

  function scheduleReorder(reason) {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      document.querySelectorAll('product-gallery').forEach(function (gallery) {
        try {
          reorderGallery(gallery, reason);
        } catch (err) {
          if (window.console && console.debug) {
            console.debug('[rubik-packshot-order]', err);
          }
        }
      });
    }, DEBOUNCE_MS);
  }

  function getSelectedVariantId(gallery) {
    var formId = gallery.getAttribute('form');
    var form = (formId && document.forms[formId]) || document.querySelector('form[id^="product-form"]');
    if (!form) return null;
    var input = form.querySelector('[name="id"]');
    if (!input || !input.value) return null;
    return String(input.value);
  }

  function readRubikPayload(productId) {
    var el =
      document.getElementById('moment-rubik-variant-images-' + productId) ||
      document.getElementById('moment-rubik-variant-images');
    if (!el) return null;
    try {
      var raw = JSON.parse(el.textContent || 'null');
      if (!raw) return null;
      if (typeof window.rubikExpandVariantImages === 'function') {
        try {
          return window.rubikExpandVariantImages(raw) || raw;
        } catch (e) {
          return raw;
        }
      }
      return raw;
    } catch (e) {
      return null;
    }
  }

  function assignedMediaIds(gallery, variantId) {
    if (!variantId) return null;
    var productId = gallery.getAttribute('data-product-id') || '';
    var data = readRubikPayload(productId);
    if (!data) return null;

    var byVariant = data.mediaIdsPerVariantId || {};
    var assigned = byVariant[variantId] || byVariant[String(variantId)];
    if (assigned && assigned.length) {
      return assigned.map(String);
    }

    var featuredMap = data.featuredMediaIdPerVariantId || {};
    var featured = featuredMap[variantId] || featuredMap[String(variantId)];
    if (featured) return [String(featured)];

    return null;
  }

  function stampThumbnails(gallery, carousel) {
    if (stamped.has(gallery)) return;
    var cells = Array.from(carousel.querySelectorAll(MEDIA_SEL));
    gallery.querySelectorAll('carousel-navigation').forEach(function (nav) {
      var buttons = Array.from(nav.querySelectorAll(THUMB_SEL));
      buttons.forEach(function (btn, index) {
        if (!btn.getAttribute('data-media-id') && cells[index]) {
          btn.setAttribute('data-media-id', cells[index].getAttribute('data-media-id') || '');
        }
      });
    });
    stamped.add(gallery);
  }

  function reorderControls(gallery, orderedIds) {
    gallery.querySelectorAll('carousel-navigation').forEach(function (nav) {
      var buttons = Array.from(nav.querySelectorAll(THUMB_SEL));
      if (!buttons.length) return;

      var byId = {};
      buttons.forEach(function (btn) {
        var id = String(btn.getAttribute('data-media-id') || '');
        if (id && !byId[id]) byId[id] = btn;
      });

      var frag = document.createDocumentFragment();
      var used = new Set();
      orderedIds.forEach(function (id) {
        var btn = byId[String(id)];
        if (btn) {
          frag.appendChild(btn);
          used.add(btn);
        }
      });
      buttons.forEach(function (btn) {
        if (!used.has(btn)) frag.appendChild(btn);
      });
      nav.appendChild(frag);
    });
  }

  function cellsInOrder(carousel) {
    // Prefer live children (Stretch allCells getter) so DOM order === carousel order.
    var children = Array.from(carousel.children || []);
    var media = children.filter(function (el) {
      return el.matches && el.matches(MEDIA_SEL);
    });
    if (media.length) return media;
    return Array.from(carousel.querySelectorAll(MEDIA_SEL));
  }

  function sameIdOrder(cells, orderedIds) {
    var visible = cells.filter(function (cell) {
      return !cell.hasAttribute('hidden');
    });
    if (visible.length < orderedIds.length) return false;
    for (var i = 0; i < orderedIds.length; i++) {
      if (String(visible[i].getAttribute('data-media-id')) !== String(orderedIds[i])) {
        return false;
      }
    }
    return true;
  }

  function refreshCarousel(carousel) {
    var allCells = cellsInOrder(carousel);
    var filteredIndexes = [];
    var visibleIndex = 0;

    allCells.forEach(function (cell, index) {
      if (cell.hasAttribute('hidden')) {
        filteredIndexes.push(index);
      } else {
        var lightBoxButton = cell.querySelector('open-lightbox-button');
        if (lightBoxButton) {
          lightBoxButton.setAttribute('image-index', String(visibleIndex));
        }
        visibleIndex += 1;
      }
    });

    busy = true;
    try {
      if (typeof carousel.filter === 'function') {
        carousel.filter(filteredIndexes);
      }
      if (typeof carousel.select === 'function') {
        carousel.select(0, { instant: true });
      }
    } finally {
      // Allow mutation/filter listeners on the next tick only.
      setTimeout(function () {
        busy = false;
      }, 0);
    }

    setTimeout(function () {
      try {
        if (typeof carousel.select === 'function') {
          carousel.select(0, { instant: true });
        }
      } catch (e) {
        /* ignore */
      }
    }, 320);
  }

  function reorderGallery(gallery, reason) {
    if (busy) return;

    var carousel =
      gallery.querySelector('scroll-carousel.product-gallery__carousel') ||
      gallery.querySelector('scroll-carousel');
    if (!carousel) return;

    var cells = cellsInOrder(carousel);
    if (!cells.length) return;

    var hiddenCount = cells.filter(function (c) {
      return c.hasAttribute('hidden');
    }).length;
    // Without Rubik (or any filter), leave Shopify media order alone.
    if (hiddenCount === 0) return;

    var variantId = getSelectedVariantId(gallery);
    var orderedIds = assignedMediaIds(gallery, variantId);
    if (!orderedIds || !orderedIds.length) return;

    stampThumbnails(gallery, carousel);

    // Map media nodes by id (first wins).
    var byId = {};
    cells.forEach(function (cell) {
      var id = String(cell.getAttribute('data-media-id') || '');
      if (id && !byId[id]) byId[id] = cell;
    });

    var assignedNodes = [];
    orderedIds.forEach(function (id) {
      var node = byId[String(id)];
      if (node) assignedNodes.push(node);
    });
    if (!assignedNodes.length) return;

    // Already in clean Rubik order at the front of the carousel.
    var domIds = cells.map(function (cell) {
      return String(cell.getAttribute('data-media-id') || '');
    });
    var assignedAtFront = orderedIds.every(function (id, i) {
      return domIds[i] === String(id);
    });
    if (assignedAtFront && sameIdOrder(cells, orderedIds)) {
      var first = cells.find(function (c) {
        return !c.hasAttribute('hidden');
      });
      if (first && String(first.getAttribute('data-media-id')) === String(orderedIds[0])) {
        if (typeof carousel.select === 'function' && carousel.selectedIndex !== 0) {
          busy = true;
          try {
            carousel.select(0, { instant: true });
          } finally {
            setTimeout(function () {
              busy = false;
            }, 0);
          }
        }
        return;
      }
    }

    var assignedSet = new Set(assignedNodes);
    var assignedIdSet = new Set(orderedIds.map(String));
    var remainder = cells.filter(function (cell) {
      return !assignedSet.has(cell);
    });

    // Align visibility to Rubik assignment so a stale Stretch/Rubik
    // filter(indexes) after DOM moves cannot leave the packshot hidden
    // or a non-assigned node visible ahead of it.
    assignedNodes.forEach(function (node) {
      node.removeAttribute('hidden');
      if (node.style && node.style.display === 'none') {
        node.style.removeProperty('display');
      }
    });
    remainder.forEach(function (node) {
      var id = String(node.getAttribute('data-media-id') || '');
      if (!assignedIdSet.has(id)) {
        node.setAttribute('hidden', '');
      }
    });

    // Physical DOM order: Rubik assignment sequence first, then everything else.
    var frag = document.createDocumentFragment();
    assignedNodes.forEach(function (node) {
      frag.appendChild(node);
    });
    remainder.forEach(function (node) {
      frag.appendChild(node);
    });
    carousel.appendChild(frag);

    // Match thumbs/dots to the same media-id sequence (assigned + remainder).
    var fullOrder = assignedNodes.concat(remainder).map(function (node) {
      return String(node.getAttribute('data-media-id') || '');
    });
    reorderControls(gallery, fullOrder);

    // Hide non-assigned thumbs to match media.
    gallery.querySelectorAll('carousel-navigation').forEach(function (nav) {
      Array.from(nav.querySelectorAll(THUMB_SEL)).forEach(function (btn) {
        var id = String(btn.getAttribute('data-media-id') || '');
        if (!id) return;
        if (assignedIdSet.has(id)) {
          btn.removeAttribute('hidden');
        } else {
          btn.setAttribute('hidden', '');
        }
      });
    });

    refreshCarousel(carousel);

    if (window.console && console.debug) {
      console.debug(
        '[rubik-packshot-order] v2.2 reordered',
        orderedIds.length,
        'assigned; packshot',
        orderedIds[0],
        'variant',
        variantId,
        reason || ''
      );
    }
  }

  function bind() {
    if (!document.querySelector('product-gallery')) return;

    document.addEventListener(
      'carousel:filter',
      function (event) {
        if (busy) return;
        if (!event.target || !event.target.closest || !event.target.closest('product-gallery')) return;
        scheduleReorder('carousel:filter');
      },
      true
    );

    document.addEventListener(
      'variant:change',
      function () {
        scheduleReorder('variant:change');
      },
      true
    );

    document.addEventListener('change', function (event) {
      var t = event.target;
      if (t && t.getAttribute && t.getAttribute('name') === 'id') {
        scheduleReorder('input[name=id]');
      }
    });

    document.querySelectorAll('product-gallery scroll-carousel').forEach(function (carousel) {
      new MutationObserver(function () {
        if (busy) return;
        scheduleReorder('mutation');
      }).observe(carousel, {
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden']
      });
    });

    scheduleReorder('init');
    setTimeout(function () {
      scheduleReorder('init+250');
    }, 250);
    setTimeout(function () {
      scheduleReorder('init+800');
    }, 800);
    setTimeout(function () {
      scheduleReorder('init+1600');
    }, 1600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
